import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';

import Navbar from './components/Navbar';
import LeaderboardComponent from './components/Leaderboard';
import About from './pages/About';
import ChallengesPage from './pages/Challenges';
import LeaderboardPage from './pages/Leaderboard';
import Footer from './components/Footer';
import CanvasLogo from './components/canvas';

function AppContent() {
  const { isDark } = useTheme();
  
  const lightModeStyles = {
    backgroundColor: '#FFFFFF',
    color: '#374151',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden'
  };
  
  const darkModeStyles = {
    backgroundColor: 'rgba(42, 42, 42, 0.5)',
    color: '#FFFFFF',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
    borderRadius: '0.5rem',
    overflow: 'hidden'
  };
  
  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'dark bg-hack-primary' : 'bg-gray-50'}`}>
      <Navbar />
      <div className="flex-grow">
        <Routes>
          <Route path="/about" element={
            <About />
          } />
          <Route path="/challenges" element={
            <ChallengesPage />
          } />
          <Route path="/leaderboard" element={
            <LeaderboardPage />
          } />
          <Route path="/" element={
            <main>
              {/* HackBench Intro and Leaderboard Section */}
              <div className="max-w-6xl mx-auto mt-16">
                <div style={isDark ? darkModeStyles : lightModeStyles}>
                  {/* Intro */}
                  <div className="flex items-center justify-center gap-1 px-12">
                    <div className="flex-2 max-w-1xl">
                      <br></br>
                      <CanvasLogo width={200} height={200} />
                    </div>
                    <div className="flex-1 max-w-2xl">
                      <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-hack-accent' : 'text-cyan-600'}`}>HackBench</h1>
                      <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-cyan-600'}`}>Can Language Models Hack Real-World Bugs?</h2>
                    </div>
                  </div>
                  <div className="px-6 max-w-4xl mx-auto">
                    <h1 className={`text-3xl font-bold mb-4 text-center ${isDark ? 'text-hack-accent' : 'text-cyan-600'}`}>TL;DR</h1>
                    <div className="max-w-3xl mx-auto p-4 rounded-lg" style={{backgroundColor: isDark ? 'transparent' : '#FFFFFF'}}>
                      <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}} className="text-base mb-4 leading-relaxed">
                        <span style={{backgroundColor: 'transparent'}} className={`font-semibold ${isDark ? 'text-hack-accent' : 'text-cyan-600'}`}>HackBench</span> is a benchmark designed to measure the cybersecurity capabilities of LLMs using Capture The Flag (CTF) challenges modeled on real-world vulnerabilities. It evaluates an LLM's ability to find, analyze, and exploit security flaws, just like a human security researcher, or pentester.
                      </p>
                      <div className="space-y-4 text-base">
                        <div className="flex gap-3">
                          <div className={`flex-none mt-1.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white' : 'bg-cyan-600'}`}></div>
                          <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}}>
                            Challenges range from basic security tasks to reversing patches, crafting exploits, and even finding n-days.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <div className={`flex-none mt-1.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white' : 'bg-cyan-600'}`}></div>
                          <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}}>
                            Previous cybersecurity benchmarks focus on abstract CTF puzzles that don't translate well to real-world security work. HackBench challenges are created with industry experts and modeled after real security audits, bug bounty reports, and CVEs, ensuring practical relevance.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <div className={`flex-none mt-1.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white' : 'bg-cyan-600'}`}></div>
                          <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}}>
                            HackBench is structured in levels. The first level consists of <b>16 challenges</b>, testing whether LLMs can operate at a <b>Security Intern</b> level without human help.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <div className={`flex-none mt-1.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white' : 'bg-cyan-600'}`}></div>
                          <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}}>
                            As models improve, new levels will be introduced, increasing in complexity. Even if an LLM has seen some challenges before due to test-set contamination, solving them without assistance still proves real skill.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <div className={`flex-none mt-1.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white' : 'bg-cyan-600'}`}></div>
                          <p style={{backgroundColor: 'transparent', color: isDark ? 'white' : '#374151'}}>
                            To submit your model or agent score. Check <Link to="/about" style={{backgroundColor: 'transparent'}} className={`${isDark ? 'text-hack-accent hover:text-hack-accent/80' : 'text-cyan-600 hover:text-cyan-700'} underline focus:outline-none`}>about</Link> section for instructions.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Leaderboard */}
                  <div className="px-8 py-12">
                    <LeaderboardComponent />
                  </div>
                </div>
              </div>
            </main>
          } />
        </Routes>
        <Footer />
      </div>
    </div>
  );
}

const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </Router>
  );
}

export default App;